import { useContext, useMemo } from "react";
import { ReferencePluginsContext } from "@adsk/acc-issues-core/providers/ReferencePickerFieldContext";
import photoPlugin from "@adsk/acc-reference-picker/es/plugins/photo";
import docsPlugin from "@adsk/acc-reference-picker/es/plugins/docs";
import sheetsPlugin from "@adsk/acc-reference-picker/es/plugins/sheets";
import rfisPlugin from "@adsk/acc-rfis/es/reference-picker-plugin";
import {
  scheduleActivityPlugin,
  planTaskPlugin,
} from "@adsk/acc-reference-picker/es/plugins/schedule";
import {
  default as formPlugin,
  formFieldsPlugin,
} from "@adsk/acc-reference-picker/es/plugins/forms";
import meetingPlugin from "@adsk/acc-reference-picker/es/plugins/meeting-item";
import submittalPlugin from "@adsk/acc-reference-picker/es/plugins/submittals";
import { pcoPlugin } from "@adsk/acc-reference-picker/es/plugins/cost/change-order";
import issuesPlugin from "../../reference-picker-plugin";
import { importRemote } from "@adsk/exo-skeleton-api";
import {
  AssetsReferencePickerPlugin,
  SystemsReferencePickerPlugin,
} from "@adsk/acs-assets-shared-web";
import {
  useFeatureFlag,
  FeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";

interface RemotePluginConfig {
  /** Key used to look up the resolved plugin. */
  key: string;
  /** importRemote scope */
  scope: string;
  /** importRemote module path */
  module: string;
}

// ---------------------------------------------------------------------------
// Remote plugin config — to consume a new remote plugin – add an entry here.
// ---------------------------------------------------------------------------
const remotePluginConfigs: RemotePluginConfig[] = [
  {
    key: "specifications",
    scope: "accSpecs",
    module: "./SpecificationsReferencePickerPlugin",
  },
  {
    key: "correspondence",
    scope: "accCorrespondence",
    module: "./CorrespondenceReferencePickerPlugin",
  },
  // {
  //   key: "reviews",
  //   scope: "accDocs",
  //   module: "./ReferencePickerReviewsPlugin",
  // },
];

function loadRemotePlugin(config: RemotePluginConfig): Promise<unknown> | null {
  if ((window as any).__DISABLE_ISSUES_REMOTES__) {
    return null;
  }

  return importRemote<{ default: unknown }>({
    scope: config.scope,
    module: config.module,
  })
    .then(({ default: plugin }) => plugin)
    .catch(() => null);
}

type RemotePlugins = Record<string, unknown>;

const results = await Promise.allSettled(
  remotePluginConfigs.map(loadRemotePlugin)
);

const resolvedRemotePlugins: RemotePlugins = {};
results.forEach((result, index) => {
  resolvedRemotePlugins[remotePluginConfigs[index].key] =
    result.status === "fulfilled" ? result.value : null;
});

const remote = (key: string) => resolvedRemotePlugins?.[key];

export const useReferencePickerPlugins = () => {
  const plugins = useContext(ReferencePluginsContext);
  const { value: useIssuesPlugin } = useFeatureFlag(
    FeatureFlag.ReferencePickerIssuesPlugin
  );
  const { value: useSheetsPlugin } = useFeatureFlag(
    FeatureFlag.ReferencePickerSheetsPlugin
  );
  const { value: usePlanTaskPlugin } = useFeatureFlag(
    FeatureFlag.ReferencePickerPlanTaskPlugin
  );
  const { value: useSpecificationsPlugin } = useFeatureFlag(
    FeatureFlag.ReferencePickerSpecificationsPlugin
  );
  const { value: useSystemsPlugin } = useFeatureFlag(
    FeatureFlag.ReferencePickerSystemsPlugin
  );
  const { value: useCorrespondencePlugin } = useFeatureFlag(
    FeatureFlag.ReferencePickerCorrespondencePlugin
  );
  const { value: useReviewsPlugin } = useFeatureFlag(
    FeatureFlag.ReferencePickerReviewsPlugin
  );

  const defaultPlugins = useMemo(
    () =>
      [
        useIssuesPlugin && issuesPlugin,
        photoPlugin,
        docsPlugin,
        useSheetsPlugin && sheetsPlugin,
        scheduleActivityPlugin,
        usePlanTaskPlugin && planTaskPlugin,
        rfisPlugin,
        AssetsReferencePickerPlugin,
        useCorrespondencePlugin && remote("correspondence"),
        useSystemsPlugin && SystemsReferencePickerPlugin,
        meetingPlugin,
        submittalPlugin,
        useReviewsPlugin && remote("reviews"),
        pcoPlugin,
        formPlugin,
        formFieldsPlugin,
        useSpecificationsPlugin && remote("specifications"),
      ].filter(Boolean),
    [
      useIssuesPlugin,
      usePlanTaskPlugin,
      useSheetsPlugin,
      useSystemsPlugin,
      useSpecificationsPlugin,
      useCorrespondencePlugin,
      useReviewsPlugin,
    ]
  );

  return plugins ?? defaultPlugins;
};
